import { isRecord } from '@/shared/utils/isRecord';
import type {
  Announcement,
  AnnouncementArchive,
  AnnouncementCopy,
  AnnouncementNoteView,
  ReleaseAnnouncements,
  ReleaseNoteCategory,
  VersionManifest,
} from '@/viewmodel/shared/versionCheck/types';

const NOTE_CATEGORIES: readonly ReleaseNoteCategory[] = [
  'ux',
  'performance',
  'agent',
  'fix',
  'security',
  'breaking',
  'internal',
  'other',
];

export function toVersionManifest(value: unknown): VersionManifest | null {
  if (!isRecord(value)) return null;
  const { version, builtAt } = value;
  if (typeof version !== 'string' || version.length === 0) return null;
  return { version, ...(typeof builtAt === 'string' ? { builtAt } : {}) };
}

function toCopy(value: unknown): AnnouncementCopy | null {
  if (!isRecord(value)) return null;
  const { title, body } = value;
  if (typeof title !== 'string' || title.length === 0) return null;
  if (typeof body !== 'string' || body.length === 0) return null;
  return { title, body };
}

function toAnnouncement(value: unknown): Announcement | null {
  if (!isRecord(value)) return null;
  const { category, ...rest } = value;
  const copy: Record<string, AnnouncementCopy> = {};
  for (const [locale, block] of Object.entries(rest)) {
    const parsed = toCopy(block);
    if (parsed) copy[locale] = parsed;
  }
  if (Object.keys(copy).length === 0) return null;
  return {
    category: NOTE_CATEGORIES.find((c) => c === category) ?? 'other',
    copy,
  };
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function toDate(value: unknown): string {
  if (typeof value !== 'string' || !ISO_DATE_RE.test(value)) return '';
  return Number.isNaN(new Date(`${value}T00:00:00`).getTime()) ? '' : value;
}

function toRelease(value: unknown): ReleaseAnnouncements | null {
  if (!isRecord(value)) return null;
  const { version, date, notes } = value;
  if (typeof version !== 'string' || version.length === 0) return null;
  if (!Array.isArray(notes)) return null;
  const parsed = notes.map(toAnnouncement).filter((n) => n !== null);
  if (parsed.length === 0) return null;
  return { version, date: toDate(date), notes: parsed };
}

export function toArchive(value: unknown): AnnouncementArchive | null {
  if (!isRecord(value)) return null;
  const { releases } = value;
  if (!Array.isArray(releases)) return null;
  return { releases: releases.map(toRelease).filter((r) => r !== null) };
}

export function copyFor(note: Announcement, language: string): AnnouncementCopy | null {
  const base = language.split('-')[0] ?? language;
  return note.copy[language] ?? note.copy[base] ?? note.copy['en'] ?? null;
}

export function toAnnouncementNotes(
  notes: Announcement[],
  language: string,
): AnnouncementNoteView[] {
  return notes.flatMap((note, index) => {
    const copy = copyFor(note, language);
    return copy ? [{ key: `${note.category}-${index}`, category: note.category, ...copy }] : [];
  });
}
